/* eslint-disable no-unused-vars */
import { useEffect, useMemo, memo, useRef } from 'react';
import { Divider } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Loader } from 'components';
import LinkSection_v1 from './components/linkSection/LinkSection_v1';
import CustomTokenEvents_v1 from './components/tokenSection/CustomTokenEvents_v1';
import Reattribution from './components/reattribution/Reattribution';
import FinalUrl from './components/finalUrl/FinalUrl';
import ListParams from './components/listParams/ListParams';
import { createEventCallback } from 'utils';
import { isEqual } from 'lodash-es';
import { useFacade } from './useFacade';
import uuid from 'react-uuid';
import { ErrorBoundary } from 'components';
import { reattribution } from 'constants';
import { creativesActions } from 'store/actions';
import { useComputed, useSignalEffect, useSignal } from '@preact/signals-react';
import {
  convertUrlToEventCallbacks,
  getCleanUrl,
  createFinalUrl,
  createCreativesListAndTrackingForDefaultItem,
} from 'utils';
import { saveCampaignServiceAPI } from 'services/saveCampaign.service';

const fieldName = {
  Tracking_Link: 'tracking_domains',
  VTA_Link: 'vta_domains',
  Deep_Link: 'deeplink_domains',
  Impression_Link: 'impression_domains',
};

const extraFieldName = {
  Tracking_Link: 'deeplink_domains',
  VTA_Link: 'impression_domains',
  Deep_Link: 'tracking_domains',
  Impression_Link: 'vta_domains',
};

const getAdjustDomains = (linkValidator, field) => {
  const validatorObj = linkValidator.find(({ tc_number }) => tc_number === 4);
  const currentField = fieldName[field];
  const extraCurrentField = extraFieldName[field];
  const domainsObj =
    validatorObj[currentField] !== null
      ? validatorObj[currentField]
      : validatorObj[extraCurrentField];
  return domainsObj?.map(({ tc_domain_name }) => tc_domain_name);
};

const EditCampaign = memo(
  ({ Deep_Link, Impression_Link, Tracking_Link, VTA_Link, linkUpdateAction, update_tc_number }) => {
    const {
      dataTrakingDeepUrl: [TrakingDeepName, TrakingDeepLink] = ['hiddenLink', ''],
      dataImpressionVTAUrl: [ImpressionVTAName, ImpressionVTALink] = ['hiddenLink', ''],
      clearTrackingError,
    } = useFacade(Deep_Link, Impression_Link, Tracking_Link, VTA_Link);

    const TrakingDeepName$ = useSignal(TrakingDeepName);
    const ImpressionVTAName$ = useSignal(ImpressionVTAName);

    const TrakingDeepLink$ = useSignal(TrakingDeepLink);
    const ImpressionVTALink$ = useSignal(ImpressionVTALink);

    useEffect(() => {
      TrakingDeepName$.value = TrakingDeepName;
      ImpressionVTAName$.value = ImpressionVTAName;
    }, [TrakingDeepName, ImpressionVTAName]);

    const currentBn_bannerid = useSelector(
      (state) => state?.creatives?.selectedCreative?.bn_bannerid,
    );

    useEffect(() => {
      TrakingDeepLink$.value = TrakingDeepLink;
    }, [TrakingDeepLink, TrakingDeepLink$]);

    useEffect(() => {
      ImpressionVTALink$.value = ImpressionVTALink;
    }, [ImpressionVTALink, ImpressionVTALink$]);

    const linkValidator = useSelector(
      (state) => state?.creatives?.creativeAndTrackingData?.link_validator_data,
    );

    const bidType = useSelector(
      (state) => state?.general?.mainCampaignObject?.currentCampaignObj?.bidType,
    );

    const trackingDeepAdjustDomains = useMemo(
      () => getAdjustDomains(linkValidator, TrakingDeepName$.value),
      [],
    );

    const impressionVtaAdjustDomains = useMemo(
      () => getAdjustDomains(linkValidator, ImpressionVTAName$.value),
      [],
    );

    const trackingPartners = useMemo(
      () =>
        linkValidator
          ?.filter((item) => {
            if (bidType == 3) return item.cpi_enabled;
            if (bidType == 2) return item.cpc_enabled;
            if (bidType == 1) return item.cpc_enabled; // cpm same as cpc
          })
          .map((item) => item.tc_name)
          .join(', ') || '',
      [linkValidator, bidType],
    );

    const cleanImpressionVTAUrl$ = useComputed(() =>
      getCleanUrl(ImpressionVTALink$.value, trackingDeepAdjustDomains),
    );
    const cleanTrakingDeepUrl$ = useComputed(() =>
      getCleanUrl(TrakingDeepLink$.value, trackingDeepAdjustDomains),
    );

    const TrakingDeepCleanLink$ = useSignal(cleanTrakingDeepUrl$.peek());
    const ImpressionVTACleanLink$ = useSignal(cleanImpressionVTAUrl$.peek());

    const adjustDomains = useMemo(() => {
      const currentValid = linkValidator.find((item) => item.tc_name === 'Adjust');
      const TrakingDeepDomains = currentValid[fieldName[TrakingDeepName]]
        ? currentValid[fieldName[TrakingDeepName]]
        : currentValid[extraFieldName[TrakingDeepName]];
      const ImpressionVTADomains = currentValid[fieldName[ImpressionVTAName]]
        ? currentValid[fieldName[ImpressionVTAName]]
        : currentValid[extraFieldName[ImpressionVTAName]];
      return {
        [TrakingDeepName]: TrakingDeepDomains?.map((item) => item.tc_domain_name),
        [ImpressionVTAName]: ImpressionVTADomains?.map((item) => item.tc_domain_name),
      };
    }, [linkValidator, TrakingDeepName, ImpressionVTAName]);

    const isAdjust$ = useComputed(() => {
      let result = { [TrakingDeepName$.value]: false, [ImpressionVTAName$.value]: false };
      try {
        const TrakingDeepURL = new URL(TrakingDeepCleanLink$.value);
        const currentDomainTD = TrakingDeepURL.host;
        const isAdjustLink = adjustDomains[TrakingDeepName$.value].some(
          (link) => currentDomainTD.includes(link) || link.includes(currentDomainTD),
        );

        result = {
          ...result,
          [TrakingDeepName$.value]: isAdjustLink,
        };
        return result;
      } catch (e) {
        try {
          const ImpressionVTAURL = new URL(ImpressionVTACleanLink$.value);
          const currentDomainIVTA = ImpressionVTAURL.host;
          const isVTALink = adjustDomains[ImpressionVTAName$.value].some(
            (link) => currentDomainIVTA.includes(link) || link.includes(currentDomainIVTA),
          );
          result = {
            ...result,
            [ImpressionVTAName$.value]: isVTALink,
          };
          return result;
        } catch (e) {
          return result;
        }
      }
    });

    const tc_number$ = useSignal('');

    // Keep track of Click link to validate Impression/VTA link
    const click_tc_number = useMemo(() => {
      const clickLink = TrakingDeepCleanLink$.value;
      const name = TrakingDeepName$.value;

      const domainObj = linkValidator?.find((item) => {
        let domainsArr = item[fieldName[name]] ? item[fieldName[name]] : item[extraFieldName[name]];
        domainsArr = domainsArr?.map((domain) => domain.tc_domain_name);

        return domainsArr?.some((link) => clickLink?.includes(link));
      });

      return domainObj?.tc_number;
    }, [TrakingDeepCleanLink$.value, TrakingDeepName$.value, linkValidator]);

    useSignalEffect(() => {
      if (!TrakingDeepCleanLink$.value && !ImpressionVTACleanLink$.value) {
        tc_number$.value = '';
      }
    });

    const matchingDomains$ = useComputed(() => {
      const currentValid = linkValidator?.find((item) => item.tc_number === tc_number$.value);
      if (currentValid) {
        const TrakingDeepDomains =
          currentValid[fieldName[TrakingDeepName]] ?? currentValid[extraFieldName[TrakingDeepName]];
        const ImpressionVTADomains =
          currentValid[fieldName[ImpressionVTAName]] ??
          currentValid[extraFieldName[ImpressionVTAName]];

        const resTrakingDeepDomains = TrakingDeepDomains?.map((item) => item.tc_domain_name);
        const resImpressionVTADomains = ImpressionVTADomains?.map((item) => item.tc_domain_name);
        return {
          [TrakingDeepName]: resTrakingDeepDomains,
          [ImpressionVTAName]: resImpressionVTADomains,
        };
      }
    });

    useSignalEffect(() => {
      if (TrakingDeepCleanLink$.peek() !== cleanTrakingDeepUrl$.value) {
        TrakingDeepCleanLink$.value = cleanTrakingDeepUrl$.value;
      }
    });
    useSignalEffect(() => {
      if (ImpressionVTACleanLink$.peek() !== cleanImpressionVTAUrl$.value) {
        ImpressionVTACleanLink$.value = cleanImpressionVTAUrl$.value;
      }
    });
    const hasReattributionTrakingDeep$ = useComputed(() =>
      TrakingDeepLink$.value?.includes(reattribution[0]),
    );
    const isReattribution$ = useSignal(hasReattributionTrakingDeep$.value);

    useSignalEffect(() => {
      if (isReattribution$.peek() !== hasReattributionTrakingDeep$.value) {
        isReattribution$.value = hasReattributionTrakingDeep$.value;
      }
    });

    const events$ = useSignal(
      TrakingDeepLink$.value
        ? convertUrlToEventCallbacks(TrakingDeepLink$.value, trackingDeepAdjustDomains)
        : [],
    );

    useSignalEffect(() => {
      events$.value = TrakingDeepLink$.value
        ? convertUrlToEventCallbacks(TrakingDeepLink$.value, trackingDeepAdjustDomains)
        : [];
    });

    const finalImpressionVTAUrl$ = useComputed(() => {
      return createFinalUrl(ImpressionVTACleanLink$, events$, isReattribution$);
    });

    const finalTrakingDeepUrl$ = useComputed(() => {
      return createFinalUrl(
        TrakingDeepCleanLink$,
        events$,
        isReattribution$,
        trackingDeepAdjustDomains,
      );
    });

    useSignalEffect(() => {
      update_tc_number(tc_number$.value, currentBn_bannerid);
    });

    const onBlurActionImpressionVTA = () => {
      linkUpdateAction(finalImpressionVTAUrl$.value, ImpressionVTAName, currentBn_bannerid);
      //update_tc_number(tc_number$.value, currentBn_bannerid);
    };
    const onBlurActionTrakingDeep = () => {
      linkUpdateAction(finalTrakingDeepUrl$.value, TrakingDeepName, currentBn_bannerid);
      //update_tc_number(tc_number$.value, currentBn_bannerid);
    };

    const onBlurTokenEvent = (elem) => {
      const { event_callback_, event_name } = elem;
      if (event_callback_ && event_name) {
        linkUpdateAction(finalImpressionVTAUrl$.value, ImpressionVTAName, currentBn_bannerid);
        linkUpdateAction(finalTrakingDeepUrl$.value, TrakingDeepName, currentBn_bannerid);
        //update_tc_number(tc_number$.value, currentBn_bannerid);
      }
    };

    const isAdjustLink = isAdjust$.value[TrakingDeepName] || isAdjust$.value[ImpressionVTAName];

    const loadedCreativeAndTrackingData = useSelector(
      (state) => state?.creatives?.creativeAndTrackingData,
    );
    const creativesTracking = useSelector(
      (state) => state?.creatives?.creativeAndTrackingData?.creativesTracking,
    );

    if (creativesTracking === undefined) {
      return <Loader.Local delay={300} />;
    }

    if (!loadedCreativeAndTrackingData) return null;

    return (
      <>
        {TrakingDeepName$.value !== 'hiddenLink' && (
          <LinkSection_v1
            matchingDomains$={matchingDomains$}
            tc_number$={tc_number$}
            click_tc_number={click_tc_number}
            cleanData$={TrakingDeepCleanLink$}
            onChange={(newUrl) => {
              TrakingDeepCleanLink$.value = newUrl;
              clearTrackingError();
            }}
            name={TrakingDeepName}
            onBlurAction={onBlurActionTrakingDeep}
            style={{ marginBottom: '16px' }}
            isSecondSection={false}
            trackingPartners={trackingPartners}
          />
        )}
        {/* {isAdjust$.value[TrakingDeepName] && (
              <ListParams name={TrakingDeepName} cleanData$={TrakingDeepCleanLink$} />
            )} */}
        <ListParams name={TrakingDeepName} cleanData$={TrakingDeepCleanLink$} />
        {isAdjustLink && (
          <CustomTokenEvents_v1
            onBlur={onBlurTokenEvent}
            events$={events$}
            onChange={(data) => {
              if (data.type === 'add') {
                events$.value = [...events$.peek(), createEventCallback()];
                return;
              }
              if (data.type === 'delete') {
                events$.value = events$.peek().filter((__, index) => data.index !== index);
                return;
              }
              events$.value = events$.peek().map((item, index) =>
                data.index === index
                  ? {
                      ...item,
                      [data.type === 'change-token' ? 'event_callback_' : 'event_name']: data.value,
                    }
                  : item,
              );
            }}
          />
        )}
        {ImpressionVTAName$.value !== 'hiddenLink' && (
          <LinkSection_v1
            matchingDomains$={matchingDomains$}
            tc_number$={tc_number$}
            click_tc_number={click_tc_number}
            cleanData$={ImpressionVTACleanLink$}
            onChange={(newUrl) => {
              ImpressionVTACleanLink$.value = newUrl;
              clearTrackingError();
            }}
            name={ImpressionVTAName}
            onBlurAction={onBlurActionImpressionVTA}
            style={{ marginBottom: '32px' }}
            isSecondSection={true}
            trackingPartners={trackingPartners}
          />
        )}
        <ListParams name={ImpressionVTAName} cleanData$={ImpressionVTACleanLink$} />
        {/* {isAdjust$.value[ImpressionVTAName] && (
              <ListParams name={ImpressionVTAName} cleanData$={ImpressionVTACleanLink$} />
            )} */}
        <ErrorBoundary>
          {isAdjustLink && (
            <Reattribution
              isReattribution$={isReattribution$}
              onChange={() => {
                isReattribution$.value = !isReattribution$.value;

                linkUpdateAction(
                  finalImpressionVTAUrl$.value,
                  ImpressionVTAName,
                  currentBn_bannerid,
                );
                linkUpdateAction(finalTrakingDeepUrl$.value, TrakingDeepName, currentBn_bannerid);
              }}
            />
          )}
        </ErrorBoundary>
        {isAdjustLink && (
          <>
            <Divider
              sx={{
                marginLeft: '-40px',
                marginRight: '-40px',
                marginBottom: '32px',
                border: '1px 0 solid #E2E8F0',
              }}
            />
            <FinalUrl
              key={`FinalUrl_${uuid()}`}
              field={TrakingDeepName}
              link={finalTrakingDeepUrl$.value}
            />
            <FinalUrl
              key={`FinalUrl_${uuid()}`}
              field={ImpressionVTAName}
              link={finalImpressionVTAUrl$.value}
            />
          </>
        )}
      </>
    );
  },
);

const NewCampaign_empty = memo(
  ({
    isFetch,
    Deep_Link,
    Impression_Link,
    Tracking_Link,
    VTA_Link,
    linkUpdateAction,
    update_tc_number,
  }) => {
    const [action, result] =
      saveCampaignServiceAPI.useLazyGetCreativesAndTrackingData_new_emptyQuery();
    // second request to fetch only link_state_model_dic part when bidType changes
    const [linkStateModelAction, linkStateModelResult] =
      saveCampaignServiceAPI.useLazyGetLinkStateModelQuery();

    const dispatch = useDispatch();

    const resetTrackingAndCreativeFlag = useSelector(
      (state) => state?.creatives?.resetTrackingAndCreativeFlag,
    );
    const app = useSelector((state) => state?.general?.mainCampaignObject?.currentCampaignObj?.app);
    const isOnBoarding = useSelector(
      (state) => state?.general?.mainCampaignObject?.currentCampaignObj?.isOnBoarding,
    );
    const campaignType = useSelector(
      (state) => state?.general?.mainCampaignObject?.currentCampaignObj?.campaignType,
    );
    const bidType = useSelector(
      (state) => state?.general?.mainCampaignObject?.currentCampaignObj?.bidType,
    );
    const reEngageGoal = useSelector(
      (state) => state?.general?.mainCampaignObject?.currentCampaignObj?.reEngageGoal,
    );
    const acquireNewUserGoal = useSelector(
      (state) => state?.general?.mainCampaignObject?.currentCampaignObj?.acquireNewUserGoal,
    );
    const target_type = useSelector(
      (state) => state?.general?.mainCampaignObject?.currentCampaignObj?.target_type,
    );
    const customAudience_IsInclude = useSelector(
      (state) => state?.general?.mainCampaignObject?.currentCampaignObj?.customAudience_IsInclude,
    );
    const customAudienceObj = useSelector(
      (state) => state?.general?.mainCampaignObject?.currentCampaignObj?.customAudienceObj,
    );
    const creativeAndTrackingData = useSelector(
      (state) => state?.creatives?.creativeAndTrackingData,
    );

    const isDataCompleted = useMemo(() => {
      if (app?.length > 0 && isOnBoarding !== '') {
        return true;
      } else return false;
    }, [app, isOnBoarding]);

    const showLayout = useMemo(() => {
      if (Object.keys(creativeAndTrackingData).length > 0) {
        return true;
      }
      return false;
    }, [creativeAndTrackingData]);

    const objToFetch = useRef(null);
    const isFetched = useRef(false);

    useEffect(() => {
      const newData = {
        pba_id: app,
        is_user_acquisition: acquireNewUserGoal,
        is_reengagement: reEngageGoal,
        is_custom_audience: !customAudienceObj ? false : true,
        custom_audience_is_include: customAudience_IsInclude,
        target_type,
        is_onboarding: isOnBoarding,
        cm_campaign_type: campaignType,
        bid_type: bidType,
      };
      if (
        isFetch &&
        isDataCompleted &&
        !isFetched.current &&
        !isEqual(result?.data?.originalArgs, newData) &&
        resetTrackingAndCreativeFlag
      ) {
        (async () => {
          try {
            objToFetch.current = newData;
            const res = await action(newData);
            if (res?.data) {
              console.log('API', res?.data);
              dispatch(creativesActions.putCreativeAndTrackingDataToStore(res?.data));
              createCreativesListAndTrackingForDefaultItem(res.data);
              dispatch(creativesActions.resetTrackingAndCreativeFlag());
              isFetched.current = true;
            }
          } catch (error) {
            console.error('Failed to perform action', error);
          }
        })();
      }
      if (isEqual(objToFetch.current, newData) && isFetched.current) {
        isFetched.current = false;
      }
    }, [
      resetTrackingAndCreativeFlag,
      result?.data?.originalArgs,
      app,
      acquireNewUserGoal,
      reEngageGoal,
      customAudienceObj,
      customAudience_IsInclude,
      isOnBoarding,
      campaignType,
      bidType,
      target_type,
      isFetch,
      isFetched.current,
    ]);

    useEffect(() => {
      if (
        !bidType ||
        !Object.keys(creativeAndTrackingData).length > 0 ||
        !isDataCompleted ||
        linkStateModelResult.isLoading
      )
        return;

      const newData = {
        pba_id: app,
        is_user_acquisition: acquireNewUserGoal,
        is_reengagement: reEngageGoal,
        is_custom_audience: !!customAudienceObj,
        cm_campaign_type: campaignType,
        bid_type: bidType,
      };
      (async () => {
        try {
          const res = await linkStateModelAction(newData);
          if (res?.data) {
            dispatch(creativesActions.updateTrackingDataWithNewLinkStateModel(res?.data));
          }
        } catch (error) {
          console.error('Failed to perform action', error);
        }
      })();
    }, [bidType]);

    if (result.isLoading) {
      return <Loader.Global delay={20} />;
    }

    return (
      <>
        {showLayout && !linkStateModelResult.isLoading ? (
          <EditCampaign
            Deep_Link={Deep_Link}
            Impression_Link={Impression_Link}
            Tracking_Link={Tracking_Link}
            VTA_Link={VTA_Link}
            linkUpdateAction={linkUpdateAction}
            update_tc_number={update_tc_number}
          />
        ) : (
          <Loader.Local delay={20} />
        )}
      </>
    );
  },
);

EditCampaign.displayName = 'EditCampaign';
NewCampaign_empty.displayName = 'NewCampaign_empty';
const ConversionTracking = { EditCampaign, NewCampaign_empty };

export default ConversionTracking;
